import { ESPECES, type Espece, type TypeObservation } from '../domaine/types'
import {
  ESPECE_LATIN,
  ESPECE_LIBELLE,
  ESPECE_TOAST,
  OBSERVATION_LIBELLE,
  OBSERVATION_TOAST,
  T,
} from '../domaine/libelles'
import { libelleSecteur } from '../domaine/secteurs'
import { surEvenement } from '../noyau/bus'
import { lireReglage } from '../noyau/db'
import {
  cloturerPatrouille,
  compter,
  comptesDe,
  ouvrirPatrouille,
  patrouilleActive,
  type Comptes,
} from '../noyau/actions'
import { zonage } from '../noyau/zonage'
import { chrono, el, vibrer, vider } from '../ui/dom'
import { ouvrirFeuille } from '../ui/feuille'
import { montrerToast } from '../ui/toast'
import { aller } from '../ui/routeur'

/*
  Écran Patrouille — celui qui remplace le carnet.

  La dalle « +1 » est le plus gros objet de l'écran : 132 × 100 px, collée au
  bord droit, atteignable au pouce sans regarder. C'est le geste répété cent
  fois par patrouille.
*/

interface Rangee {
  type: TypeObservation
  sousTitre?: { texte: string; urgence?: boolean }
  urgence?: boolean
}

const RANGEES: Rangee[] = [
  { type: 'personne_sensibilisee' },
  { type: 'ponte', sousTitre: { texte: T.choisirEspece } },
  { type: 'trace_sans_ponte' },
  {
    type: 'emergence',
    sousTitre: { texte: T.desorientationSignaler, urgence: true },
    urgence: true,
  },
]

export function monterPatrouille(hote: HTMLElement): () => void {
  let minuteur: ReturnType<typeof setInterval> | null = null
  let vivant = true

  const desabonnements = [
    surEvenement('patrouille-changee', () => void rendre()),
    surEvenement('zonage-change', () => void rendre()),
  ]

  async function rendre(): Promise<void> {
    if (!vivant) return

    const patrouille = await patrouilleActive()
    if (!vivant) return

    if (minuteur) {
      clearInterval(minuteur)
      minuteur = null
    }

    vider(hote)

    if (!patrouille) {
      hote.append(vueSansPatrouille())
      return
    }

    const comptes = await comptesDe(patrouille.id)
    const secteurs = await zonage()
    if (!vivant) return

    const debut = new Date(patrouille.debut).getTime()
    const valeurChrono = el('div', {
      class: 'chrono',
      text: chrono((Date.now() - debut) / 1000),
    })

    hote.append(
      entete(
        valeurChrono,
        libelleSecteur(patrouille.secteur, secteurs),
        patrouille.equipe ?? '—',
      ),
      compteurs(comptes),
    )

    minuteur = setInterval(() => {
      valeurChrono.textContent = chrono((Date.now() - debut) / 1000)
    }, 1000)
  }

  function entete(
    valeurChrono: HTMLElement,
    nomSecteur: string,
    equipe: string,
  ): HTMLElement {
    return el('section', { class: 'entete' }, [
      el('div', { class: 'entete__surtitre', text: T.patrouilleEnCours.toUpperCase() }),
      valeurChrono,
      el('div', { class: 'cartouches' }, [
        cartouche(T.secteur, nomSecteur),
        cartouche(T.binome, equipe),
      ]),
    ])
  }

  function cartouche(label: string, valeur: string): HTMLElement {
    return el('div', { class: 'cartouche' }, [
      el('div', { class: 'cartouche__label', text: label }),
      el('div', { class: 'cartouche__valeur', text: valeur }),
    ])
  }

  function compteurs(comptes: Comptes): HTMLElement {
    const zone = el('section', { class: 'compteurs' })

    for (const rangee of RANGEES) {
      zone.append(rendreRangee(rangee, comptes[rangee.type]))
    }

    const cloture = el('button', {
      type: 'button',
      class: 'bouton-cloture',
      text: T.cloturerPatrouille,
    })
    cloture.addEventListener('click', demanderCloture)
    zone.append(cloture)

    return zone
  }

  function rendreRangee(rangee: Rangee, valeur: number): HTMLElement {
    const gauche = el('div', { class: 'rangee__gauche' }, [
      el('div', { class: 'rangee__titre', text: OBSERVATION_LIBELLE[rangee.type] }),
      rangee.sousTitre
        ? el('div', {
            class: rangee.sousTitre.urgence
              ? 'rangee__sous rangee__sous--urgence'
              : 'rangee__sous',
            text: rangee.sousTitre.texte,
          })
        : null,
      el('div', { class: 'rangee__valeur', text: String(valeur) }),
    ])

    const dalle = el('button', {
      type: 'button',
      class: 'dalle',
      text: '+1',
      'aria-label': `${OBSERVATION_LIBELLE[rangee.type]}, ajouter un`,
    })
    dalle.addEventListener('click', () => {
      if (rangee.type === 'ponte') ouvrirFeuilleEspece()
      else void incrementer(rangee.type, null)
    })

    return el('div', { class: rangee.urgence ? 'rangee rangee--urgence' : 'rangee' }, [
      gauche,
      dalle,
    ])
  }

  async function incrementer(type: TypeObservation, espece: Espece | null): Promise<void> {
    // Retour haptique immédiat : l'écran est souvent regardé après coup.
    vibrer(15)
    const observation = await compter(type, espece)
    if (!observation) return
    montrerToast(espece ? ESPECE_TOAST[espece] : OBSERVATION_TOAST[type])
  }

  function ouvrirFeuilleEspece(): void {
    ouvrirFeuille({
      titre: T.especeDeLaPonte,
      aide: T.especeAide,
      options: ESPECES.map((e) => ({
        cle: e,
        titre: ESPECE_LIBELLE[e],
        sousTitre: ESPECE_LATIN[e],
      })),
      surChoix: (cle) => void incrementer('ponte', cle as Espece),
    })
  }

  function demanderCloture(): void {
    ouvrirFeuille({
      titre: T.confirmerCloture,
      aide: T.confirmerClotureAide,
      actionPrincipale: {
        texte: T.cloturerPatrouille,
        surClic: () => {
          void cloturerPatrouille().then(() => montrerToast(T.clotureFaite))
        },
      },
    })
  }

  /* ── Démarrage d'une patrouille ─────────────────────────────── */

  function vueSansPatrouille(): HTMLElement {
    const bouton = el('button', {
      type: 'button',
      class: 'bouton-plein bouton-plein--grand',
      text: T.demarrerPatrouille,
    })
    bouton.addEventListener('click', () => void choisirSecteur())

    return el('section', { class: 'vide' }, [
      el('h1', { class: 'vide__titre', text: T.aucunePatrouille }),
      el('p', { class: 'vide__aide', text: T.aucunePatrouilleAide }),
      bouton,
    ])
  }

  async function choisirSecteur(): Promise<void> {
    const secteurs = await zonage()
    ouvrirFeuille({
      titre: T.choisirSecteur,
      aide: T.choisirSecteurAide,
      options: secteurs.map((s) => ({
        cle: s.code,
        titre: s.libelle,
        sousTitre: s.groupe ?? undefined,
      })),
      surChoix: (code) => void choisirBinome(code),
    })
  }

  async function choisirBinome(secteur: string): Promise<void> {
    const dernier = (await lireReglage('prenom')) ?? ''

    const champ = el('input', {
      type: 'text',
      class: 'champ',
      value: dernier,
      placeholder: 'Awa · Sylvain',
      autocomplete: 'off',
      'aria-label': T.binomeQuestion,
    }) as HTMLInputElement

    const contenu = el('div', { class: 'bloc-champ' }, [
      el('label', { class: 'champ__label', text: T.binomeQuestion }),
      champ,
      el('p', { class: 'champ__aide', text: T.binomeAide }),
    ])

    ouvrirFeuille({
      titre: T.choisirSecteur,
      contenu,
      actionPrincipale: {
        texte: T.commencer,
        surClic: () => {
          void ouvrirPatrouille(secteur, champ.value || null)
        },
      },
    })
  }

  void rendre()

  return () => {
    vivant = false
    if (minuteur) clearInterval(minuteur)
    for (const off of desabonnements) off()
  }
}

/** Raccourci utilisé par l'écran Signaler pour renvoyer vers la patrouille. */
export function allerPatrouille(): void {
  aller('patrouille')
}
